import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { enrichValidationResults, formatValidationErrorMessage } from '../../constants/validationCheckCatalog';
import { generateValidationReportPdf } from '../../utils/validationReportPdf';
import './ExportValidationDialog.css';

const STATUS_ROW_BG = {
  passed: 'rgba(76, 175, 80, 0.08)',
  failed: 'rgba(244, 67, 54, 0.08)',
  skipped: 'rgba(255, 152, 0, 0.1)',
};

const OUTCOME_COLOR = {
  passed: 'success.main',
  failed: 'error.main',
  skipped: 'warning.main',
};

const buildDetailText = (result) => {
  if (result.status === 'passed') {
    return result.successDetail || 'Passed — meets pass criteria.';
  }
  if (result.failureDetail && result.failureDetail.length > 0) {
    return result.failureDetail.map((line, i) => `${i + 1}. ${formatValidationErrorMessage(line)}`).join('\n');
  }
  if (result.status === 'skipped') return 'Skipped — prerequisites not met.';
  return 'Failed — no specific findings recorded.';
};

const ExportValidationDialog = ({
  isOpen,
  onClose,
  validationResults = [],
  isLoading = false,
  dialogTitle = 'Export Validation Report',
  dialogSubtitle = null,
  loadingMessage = 'Running validation checks...',
  infoMessage = null,
  reportMeta = {},
  enablePdfDownload = true,
}) => {
  const [pdfGenerating, setPdfGenerating] = React.useState(false);

  const enrichedResults = React.useMemo(
    () => enrichValidationResults(validationResults),
    [validationResults]
  );

  const handleDownloadPdf = () => {
    setPdfGenerating(true);
    try {
      generateValidationReportPdf({
        results: enrichedResults,
        reportTitle: dialogTitle,
        studyId: reportMeta.studyId || reportMeta.sponsorProtocolIdentifier || '',
        trialAcronym: reportMeta.trialAcronym || reportMeta.projectName || '',
        sponsorName: reportMeta.sponsorName || '',
        projectName: reportMeta.projectName || '',
        protocolNumber: reportMeta.protocolNumber || '',
        sectionCount: reportMeta.sectionCount || 0,
      });
    } catch (err) {
      console.error('PDF report generation failed:', err);
      alert(`Could not generate PDF: ${err.message}`);
    } finally {
      setPdfGenerating(false);
    }
  };

  if (!isOpen) return null;

  const skippedCount = enrichedResults.filter((r) => r.skipped).length;
  const failedCount = enrichedResults.filter((r) => r.status === 'failed').length;
  const passedCount = enrichedResults.filter((r) => r.status === 'passed').length;
  const totalCount = enrichedResults.length;
  const allPassed = failedCount === 0;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        className: 'export-validation-dialog-landscape',
        sx: {
          borderRadius: '12px',
          maxHeight: '92vh',
          width: 'min(96vw, 1400px)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: allPassed ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
              color: allPassed ? 'rgba(56, 142, 60, 0.9)' : 'rgba(198, 40, 40, 0.9)',
            }}
          >
            {allPassed ? (
              <CheckCircleIcon sx={{ fontSize: 32 }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 32 }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="div">
              {dialogTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dialogSubtitle ||
                (allPassed
                  ? skippedCount > 0
                    ? `${passedCount} passed, ${skippedCount} skipped`
                    : 'All validations passed.'
                  : `${failedCount} of ${totalCount} checks failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}`)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              {loadingMessage}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                height: 4,
                bgcolor: 'grey.200',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '60%',
                  height: '100%',
                  bgcolor: 'primary.main',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(400%)' },
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            {infoMessage && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  {infoMessage}
                </Typography>
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: allPassed ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">
                  {allPassed ? 'All Checks Passed' : 'Validation Issues Found'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${passedCount} Passed`} size="small" color="success" variant="outlined" />
                  {failedCount > 0 && (
                    <Chip label={`${failedCount} Failed`} size="small" color="error" variant="outlined" />
                  )}
                  {skippedCount > 0 && (
                    <Chip label={`${skippedCount} Skipped`} size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Results are shown in tabular format. Download the PDF for a landscape audit report with the same
                columns.
              </Typography>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: '58vh',
                overflow: 'auto',
                '& .MuiTableCell-root': {
                  verticalAlign: 'top',
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                },
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 36, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 160, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      Validation Check
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      Outcome
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 100, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      Method
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 200, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      How Checked
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 180, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      Pass Criteria
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 220, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                      Findings / Result
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrichedResults.map((result, index) => (
                    <TableRow
                      key={result.id || index}
                      sx={{ bgcolor: STATUS_ROW_BG[result.status] || 'inherit' }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{result.label}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: OUTCOME_COLOR[result.status] || 'text.primary' }}
                        >
                          {result.outcome}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={result.methodologyLabel} size="small" sx={{ height: 22 }} />
                      </TableCell>
                      <TableCell>{result.howChecked}</TableCell>
                      <TableCell>{result.passCriteria}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            m: 0,
                            color:
                              result.status === 'failed'
                                ? 'error.main'
                                : result.status === 'skipped'
                                  ? 'warning.dark'
                                  : 'success.dark',
                          }}
                        >
                          {buildDetailText(result)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!allPassed && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<InfoIcon />}>
                <Typography variant="body2">
                  Resolve failed checks before export where applicable. The landscape PDF documents all findings for
                  audit.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {enablePdfDownload && !isLoading && enrichedResults.length > 0 && (
          <Button
            onClick={handleDownloadPdf}
            variant="outlined"
            color="secondary"
            startIcon={<PdfIcon />}
            disabled={pdfGenerating}
          >
            {pdfGenerating ? 'Generating PDF…' : 'Download Landscape PDF'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportValidationDialog;
